import { useEffect, useState } from 'react';
import { PatientState } from '../../../services/redux/partner/patient';
import {
  PatientDoctorFormGroup,
  useFormFieldEffect,
  FormField,
  validateRequired,
} from '../..';

export interface PatientDoctorFormFields {
  doctorId: number | null;
}

export interface PatientDoctorFormValidation {
  isValid: boolean;
  isClean: boolean;
  isSubmitted: boolean;
  isValidRegistration: boolean;
  isValidPrescription: boolean;
  isValidDoctor: boolean;
  isValidPlan: boolean;
  isValidShipping: boolean;
  isValidBilling: boolean;
}

export const usePatientDoctorFormEffect = (
  checkoutState: PatientState
): PatientDoctorFormGroup => {
  const {
    formData,
    pageState: {
      validRegistration,
      validPrescription,
      validDoctor,
      validPlan,
      validShipping,
      validBilling,
    },
  } = checkoutState;

  const [currentPatientForm, setPatientForm] =
    useState<PatientDoctorFormValidation>({
      isValid: validDoctor,
      isClean: true,
      isSubmitted: false,
      isValidRegistration: validRegistration,
      isValidPrescription: validPrescription,
      isValidDoctor: validDoctor,
      isValidPlan: validPlan,
      isValidShipping: validShipping,
      isValidBilling: validBilling,
    });

  const doctorId = useFormFieldEffect(
    'doctorId',
    'select',
    [validateRequired],
    { required: true },
    formData.doctor?.doctorId || ''
  );

  const members = {
    doctorId,
  };

  useEffect(() => {
    let valid = true;
    let clean = true;

    const formFields = members as any as { [key: string]: FormField };

    for (const i in formFields) {
      if (!formFields[i].isValid) {
        valid = false;
      }

      if (!formFields[i].isClean) {
        clean = false;
      }
    }

    const validateDoctorResult = !(
      !formFields.doctorId.value || !formFields.doctorId.isValid
    );

    const updatedPatientForm: PatientDoctorFormValidation = {
      isValid: valid,
      isClean: clean,
      isSubmitted: currentPatientForm.isSubmitted,
      isValidRegistration: currentPatientForm.isValidRegistration,
      isValidPrescription: currentPatientForm.isValidPrescription,
      isValidDoctor: validateDoctorResult,
      isValidPlan: currentPatientForm.isValidPlan,
      isValidShipping: currentPatientForm.isValidShipping,
      isValidBilling: currentPatientForm.isValidBilling,
    };

    const updatedPatientStateString = JSON.stringify(updatedPatientForm);
    const currentPatientFormString = JSON.stringify(currentPatientForm);

    if (updatedPatientStateString !== currentPatientFormString) {
      setPatientForm(updatedPatientForm);
    }
  }, [members, currentPatientForm, setPatientForm]);

  const getFieldValues = (): PatientDoctorFormFields => {
    return {
      doctorId: (members.doctorId.value as number) || null,
    };
  };

  const resetFieldValues = (clean = false) => {
    members.doctorId.doReset(clean);
  };

  return {
    isValid: currentPatientForm.isValid,
    isClean: currentPatientForm.isClean,
    isSubmitted: currentPatientForm.isSubmitted,
    isValidDoctor: currentPatientForm.isValidDoctor,
    getFieldValues,
    resetFieldValues,
    members,
  };
};
